/**
 * @file PersonalPortfolio.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useState, useEffect, useCallback } from "react";
import AccountStatements from "../../modules/clients/AccountStatements";
import Widget from "../Widget";
import DgModal from "../../../../components/DgModal";
import Loader from "../Loader";
import FilterSelect from "../../../../components/dashboard/FilterSelect";
import DgLineChart from "../../../../components/dashboard/DgLineChart";
import { toMoney } from "../../../../utils/moneyUtils";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatAmount } from "../../../../utils/formatAmount";
import { getInitialEquityFromHistorical } from "../../../../utils/utils";
import { getSessionObject } from "../../../../utils/sessionUtils";
import Request from "../../../../utils/fetchUtils";
import Swal from "sweetalert2";
import DgButton from "../../../../components/DgButton";

function PersonalPortfolio({
  width,
  data,
  loading,
  account,
  onFilter = () => { },
  onScoreUpdate = () => { },
  onDataRefresh = () => { },
  userKey,
  fullId,
}) {
  const { historical = [] } = data;

  const userData = {
    totalCash: data.availableCash || 0,
  };

  const [statementsModal, setStatementsModal] = useState(false);
  const [equityTimeFilter, setEquityTimeFilter] = useState(null);
  const [scoreInput, setScoreInput] = useState("");
  const [scoreLoading, setScoreLoading] = useState(false);
  const [currentScore, setCurrentScore] = useState(data.score);
  const [tradierConnectionStatus, setTradierConnectionStatus] = useState('checking');
  const [reauthenticating, setReauthenticating] = useState(false);
  
 
  useEffect(() => {
    setCurrentScore(data.score);
  }, [data.score]);

  useEffect(() => {
    try {
      if (loading) {
        setTradierConnectionStatus('checking');
        return;
      }

      if (!data || Object.keys(data).length === 0) {
        setTradierConnectionStatus('checking');
        return;
      }

      // Approved-but-unlinked / pending-application states come from
      // brokerLinkStatus, not brokerConnectionStatus (which only exists once
      // a broker account is already linked) — surface them directly instead
      // of falling through to the "checking" -> "expired" timeout, which
      // would otherwise misdescribe a never-connected account as expired.
      if (
        data.brokerLinkStatus === 'needs_authorization' ||
        data.brokerLinkStatus === 'not_linked'
      ) {
        setTradierConnectionStatus(data.brokerLinkStatus);
        return;
      }

      if (!data.brokerConnectionStatus) {
        setTradierConnectionStatus('checking');
        return;
      }

      setTradierConnectionStatus(data.brokerConnectionStatus);
    } catch (error) {
      setTradierConnectionStatus('error');
    }
  }, [data, loading]);

  // Timeout separado para cambiar de checking a expired
  useEffect(() => {
    if (tradierConnectionStatus === 'checking') {
      const timeout = setTimeout(() => {
        setTradierConnectionStatus('expired');
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [tradierConnectionStatus]);

  const generateRandomState = () => {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  };

  const generateAuthorizeUrl = () => {
    const scope = "read,trade,market";
    const { REACT_APP_TRADIER_CLIENT_ID } = process.env;

    const newState = generateRandomState();
    localStorage.setItem('tradierAuthState', newState);

    const redirectUri = window.location.href.split('?')[0];

    return `https://api.tradier.com/v1/oauth/authorize?client_id=${REACT_APP_TRADIER_CLIENT_ID}&scope=${scope}&state=${newState}&redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  // Internal refresh state
  const [internalRefreshing, setInternalRefreshing] = useState(false);

  // Process authorization code after reauth
  const processAuthCode = useCallback(async (code) => {
    try {
      setInternalRefreshing(true);
      const { email, sub, role } = getSessionObject();

      const redirectUri = window.location.href.split('?')[0];

      const res = await Request(
        "/trading/access",
        {
          code,
          email,
          userId: sub,
          role,
          redirectUri
        },
        "POST"
      );

      if (res.ok) {
        // Update local session with new broker accounts
        const session = { ...getSessionObject() };
        session.brokerAccounts = res.data.brokerAccounts;
        localStorage.session = JSON.stringify(session);

        console.log('Tradier account reconnected successfully');

        // Show success message
        await Swal.fire({
          title: "Reconnection Successful",
          text: "Your Tradier account has been reconnected. Data is updating...",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });

        // Refresh dashboard data internally
        setTimeout(async () => {
          console.log('Refreshing dashboard data after reauth');
          await refreshDashboardData(true);
        }, 2000);

      } else {
        const errorMessage = res.message || "Error reconnecting account.";
        Swal.fire("Error", errorMessage, "error");
      }
    } catch (error) {
      console.error("Error processing authorization code:", error);
      Swal.fire(
        "Error",
        "An error occurred during authorization.",
        "error"
      );
    } finally {
      setInternalRefreshing(false);
    }
  }, []);

  // Internal dashboard refresh function
  const refreshDashboardData = useCallback(async (forceRefresh = false) => {
    if (!account?.id) return;

    try {
      setInternalRefreshing(true);

      // Add timestamp to avoid cache on reauth
      const timestamp = forceRefresh ? `&timestamp=${Date.now()}` : "";
      const accountQuery = `accountId=${account.id}${timestamp}`;

      const res = await Request(
        `/trading/dashboard?${accountQuery}`,
        {},
        "GET",
        { Userkey: fullId || userKey }
      );

      if (res.ok) {
        console.log('Dashboard data refreshed successfully after reauth');
        // Notify parent component to update its state
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        console.error('Error refreshing dashboard:', res);
      }
    } catch (error) {
      console.error('Dashboard refresh error:', error);
    } finally {
      setInternalRefreshing(false);
    }
  }, [account?.id, fullId, userKey, onDataRefresh]);

  // Detect authorization parameters in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const receivedState = urlParams.get("state");

    if (code && receivedState) {
      const savedState = localStorage.getItem('tradierAuthState');
      if (receivedState === savedState) {
        processAuthCode(code);
        localStorage.removeItem('tradierAuthState');
      } else {
        Swal.fire(
          "Security Error",
          "Security verification failed. Please try again.",
          "error"
        );
      }

      // Clean URL
      if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.href.split('?')[0];
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }, [processAuthCode]);



  const handleReauthentication = async () => {
    try {
      setReauthenticating(true);
      console.log('Reauthenticating...');

      if (data.hasTradierRefreshToken) {
        console.log('Attempting token refresh');
        const refreshResponse = await Request('/trading/refresh-token', {
          email: data.email,
          userId: data.userId,
          role: data.role
        }, 'POST');

        if (refreshResponse.ok && refreshResponse.data?.accessToken) {
          await refreshDashboardData(false);
          setTradierConnectionStatus('connected');
          return;
        }
        console.log('Token refresh failed', refreshResponse);
      }

      // If no refresh token, proceed with re-auth
      const result = await Swal.fire({
        title: "Reconnect Tradier",
        text: "Your Tradier session has expired. Would you like to reconnect your account to get updated data?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Reconnect Now",
        cancelButtonText: "Later",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#6c757d"
      });

      if (result.isConfirmed) {
        const authUrl = generateAuthorizeUrl();
        await Swal.fire({
          title: "Redirecting...",
          text: "You will be redirected to Tradier for authorization.",
          icon: "info",
          timer: 2000,
          showConfirmButton: false
        });
        window.location.href = authUrl;
      }
    } catch (error) {
      console.error("Error in reAuthentication:", error);
      setReauthenticating(false);
      Swal.fire("Error", "There was a problem trying to reconnect. Please try again.", "error");
    } finally {
      setReauthenticating(false);
    }
  };

  const handleRefreshData = async () => {
    await refreshDashboardData(false);
  };

  const renderTradierConnectionWarning = () => {
    if (tradierConnectionStatus === 'checking') {
      return (
        <div style={{
          padding: "12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          marginBottom: "15px",
          border: "1px solid #e9ecef",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          <InfoIcon style={{ color: "#6c757d", fontSize: "18px" }} />
          <span style={{ fontSize: "13px", color: "#6c757d" }}>
            Checking connection to Tradier...
          </span>
        </div>
      );
    }

    if (tradierConnectionStatus === 'needs_authorization') {
      return (
        <div style={{
          padding: "12px",
          backgroundColor: "#cce5ff",
          borderRadius: "6px",
          marginBottom: "15px",
          border: "1px solid #99caff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
            <InfoIcon style={{ color: "#004085", fontSize: "18px" }} />
            <span style={{ fontSize: "13px", color: "#004085" }}>
              <strong>Your Tradier application was approved:</strong> authorize access to see your balance.
            </span>
          </div>
          <DgButton
            category="primary"
            size="small"
            text="Authorize"
            action={handleReauthentication}
            loading={reauthenticating || internalRefreshing}
            style={{ fontSize: "12px", padding: "4px 12px" }}
          />
        </div>
      );
    }

    if (tradierConnectionStatus === 'not_linked') {
      return (
        <div style={{
          padding: "12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          marginBottom: "15px",
          border: "1px solid #e9ecef",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          <InfoIcon style={{ color: "#6c757d", fontSize: "18px" }} />
          <span style={{ fontSize: "13px", color: "#6c757d" }}>
            Your Tradier application is still being reviewed. We'll let you know as soon as it's approved.
          </span>
        </div>
      );
    }

    if (tradierConnectionStatus === 'expired') {
      return (
        <div style={{
          padding: "12px",
          backgroundColor: "#fff3cd",
          borderRadius: "6px",
          marginBottom: "15px",
          border: "1px solid #ffeaa7",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
            <WarningIcon style={{ color: "#856404", fontSize: "18px" }} />
            <span style={{ fontSize: "13px", color: "#856404" }}>
              <strong>Connection to Tradier expired:</strong> The information displayed may be out of sync.
            </span>
          </div>
          <DgButton
            category="warning"
            size="small"
            text="Reconnect"
            action={handleReauthentication}
            loading={reauthenticating || internalRefreshing}
            style={{ fontSize: "12px", padding: "4px 12px" }}
          />
        </div>
      );
    }

    if (tradierConnectionStatus === 'disconnected' || tradierConnectionStatus === 'error') {
      return (
        <div style={{
          padding: "12px",
          backgroundColor: "#f8d7da",
          borderRadius: "6px",
          marginBottom: "15px",
          border: "1px solid #f5c6cb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
            <WarningIcon style={{ color: "#721c24", fontSize: "18px" }} />
            <span style={{ fontSize: "13px", color: "#721c24" }}>
              <strong>Connection error:</strong> Tradier status could not be verified.
              Information may be outdated.
            </span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <DgButton
              category="secondary"
              size="small"
              text={<RefreshIcon style={{ fontSize: "16px" }} />}
              action={handleRefreshData}
              loading={loading || internalRefreshing}
              style={{ fontSize: "12px", padding: "4px 8px", minWidth: "auto" }}
              title="Refresh data"
            />
            <DgButton
              category="danger"
              size="small"
              text="Reconnect"
              action={handleReauthentication}
              loading={reauthenticating || internalRefreshing}
              style={{ fontSize: "12px", padding: "4px 12px" }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const initialEquity = getInitialEquityFromHistorical(historical);
  const lastEquityAmount = historical[historical.length - 1]?.totalEquity || 0;
  const equityAmount = parseFloat(lastEquityAmount.toFixed(2)).toLocaleString();
  const equityDifference =
    historical[historical.length - 1]?.totalEquity - initialEquity;
  const equityPercentage =
    typeof data.timeWeightedPerformance === "number"
      ? formatAmount(data.timeWeightedPerformance, 2)
      : "-";

  const handleStatementsModal = (isOpen) => {
    setStatementsModal(isOpen);
  };

  const handleEquityTimeFilter = (filter) => {
    setEquityTimeFilter(filter);
  };

  const handleResetFilters = () => {
    setEquityTimeFilter(null);
  };

  return (
    <Widget name={"personal-portfolio"} width={width}>
      <div className="heading">
        <h1 className="title">Personal Portfolio</h1>

        {/* Tradier Connection Status Warning */}
        {renderTradierConnectionWarning()}
        

        
        <>
          <p style={{ color: "#95b2c5" }}>
            <WarningIcon />
            {"  "}We value your privacy and are committed to protecting your
            personal information. We do not sell, trade, or share your
            information with third parties for marketing purposes.
          </p>
        </>
        <p className="subtitle">
          Account: {account.id || "-"} <br />
          Cash available: <span>{toMoney(userData.totalCash)}</span> <br />
          <span style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            gap: "5px",
            fontSize: "12px",
            marginTop: "5px"
          }}>
            <span style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: tradierConnectionStatus === "connected" ? "#22c55e" : "#ef4444"
            }}></span>
            {tradierConnectionStatus === "connected" ? "Synced with Tradier" : "Not synced with Tradier"}
          </span>
        </p>
      </div>

      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <FilterSelect
            onFilter={onFilter}
            sendFilterToParent={handleEquityTimeFilter}
          />
          {equityTimeFilter && (
            <button
              className="reset-filters-btn"
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>
          )}
        </div>
        {!loading ? (
          <DgLineChart
            data={historical}
            value={"totalEquity"}
            title={"Total equity"}
            height={340}
          />
        ) : (
          <Loader></Loader>
        )}

        <div className="value-info">
          <div
            className="btn gradient"
            style={{
              color:
                equityPercentage >= 0 || equityPercentage === "-"
                  ? "#000"
                  : "#ef4444",
            }}
          >
            {equityPercentage}%
          </div>
          <p className="subtitle">Current value</p>
          <h2 className="current-value">${equityAmount}</h2>
          <h5
            className={`amount-performance ${equityDifference < 0 ? "negative" : ""
              }`}
          >
            {equityDifference > 0 && "+"}
            {toMoney(equityDifference)}
          </h5>
        </div>
      </>

      {/* Account Statements Modal */}
      <DgModal
        modalState={statementsModal}
        handleModalState={handleStatementsModal}
      >
        <AccountStatements />
      </DgModal>

      {/* Score Loading */}
      {scoreLoading && <Loader />}
    </Widget>
  );
}

export default PersonalPortfolio;